import React, { useState } from 'react';
import compose from 'lodash/fp/compose';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getFavoriteOrg,
  addFavoriteOrg,
  searchOrganisation,
} from 'actions/organisation';
import {
  Switchery,
  InputDatePicker,
  Select,
  Input,
  Button,
  OrgForm,
  ButtonLink,
  NewEntriesSelect,
  Image,
  FileInput,
  AutoCompleteInput,
} from 'components/common/FormItems';
import SearchOrgResult from './SearchOrgResult';
import { getQueryParams } from './helpers/getQueryParams';
import { fetchBicSuggestions } from 'actions/bankBic';
import {
  withCountry,
  withState,
  withCity,
  withLegalForm,
  withIndustryType,
  withOrganisation,
  withOrganisationMotivation,
  withPublicRespArea,
  withOrganisationFunction,
  withOrgFunctionAssignment,
  withCurrency,
  withTranslation,
  withCustomRouter,
  withUserPreferences,
  withLanguage,
  withMsgInfo,
} from 'components/hoc';
import { useParams } from 'react-router-dom';
import API from '../../../../../utils/api';
import axios from '../../../../../axios';
import { RESPONSE_STRINGS } from '../../../../../constants';
import Swal from 'sweetalert2';
import { getResError } from '../../../../../actions/account';

const ImageWrapper = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
`;

const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const StyledInput = styled(Input)`
  padding-right: 0.5rem;
`;

const StyledAutoCompleteInput = styled(AutoCompleteInput)`
    width: 96%;
    margin-right: 2rem;
`;

const StyledSelect = styled(Select)`
  margin-right: 2rem;
`;
const StyledNewEntriesSelect = styled(NewEntriesSelect)`
  margin-right: 2rem;
`;

const StyledInputDatePicker = styled(InputDatePicker)`
  padding-right: 1rem;
`;

const StyledButton = styled(Button)`
  // width: 8.0625rem;
  width: 11rem;
  height: 2.7rem;
  line-height: 1.25rem;
  font-weight: 500;
  font-size: 1rem;
  border: solid 1px #1d579b;
  background: #4c81be;
  color: #fff;
  transition: all ease-in-out 300ms;
  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.29);
  }
`;

const status = {
  IDLE: 'IDLE',
  ONGOING: 'ONGOING',
  DONE: 'DONE',
};

const initialValues = {
  id: '',
  name: '',
  org_country: '',
  state: '',
  legal_form: '',
  register_institute: '',
  register_no: '',
  legal_personality: true,
  public: false,
  non_profit: false,
  organisation_function: '',
  school_type: '',
  BIC: '',
};

const searchOrgFormSchema = {
  name: {
    validation: 'required',
  },
};


const SearchOrgForm = ({
  trObj,
  account,
  resetForm,
  renderActionButtons,
  legalFormLocaleOptions,
  countryLocaleOptions,
  getStateByCountryId,
  getCityByStateId,
  stateLocaleOptions,
  cityLocaleOptions,
  currencyLocaleOptions,
  industryTypeOptions,
  orgOptions,
  orgMotivationLocaleOptions,
  orgFunctionLocaleOptions,
  tr,
  checkboxType,
  routeParams,
  baseSearchPath,
  basePath,
  userPreferences,
  activeLanguageOptions,
  detailMode,
  fetchingState,
  fetchingCity,
  routePathname,
  setMsgInfo,
  getSchoolsFromOrgFunctionAssignment,
  orgFunctionAssignment: { 
    register_institute_options, 
    school_options,
    credit_card_options,
   },
  ...rest
}) => {
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const { search } = useSelector((state) => state.organisation);
  const [formData, setFormData] = useState({ ...initialValues });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState(status.IDLE);
  const [addingFavoriteOrg, setAddingFavoriteOrg] = useState(false);
  const [bicOptions, setBicOptions] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getOrgFunctionLabel = (id) => {
    if (!id) return '';
    const found = orgFunctionLocaleOptions?.find((opt) => opt.id === id);
    if (!found) return '';
    if (userPreferences.language === 'de') {
      return found.long_description_deu || '';
    }
    // Default to English
    return found.long_description_eng || '';
  };

  const [showEntryForm, setShowEntryForm] = React.useState(false);
  const [entryValues, setEntryValues] = React.useState({
    value: '',
    language: userPreferences.language,
  });
  const [entrySubmitting, setEntrySubmitting] = React.useState(false);

  const createOrgForm = () => {
    const queryParams = new URLSearchParams(formData).toString();
    navigate(`/dashboard/data/base/organisation/create?${queryParams}`);
  };

  const { id } = useParams();

  
  const onChange = (e) => {
    if (formSubmitting) return;
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleBicInputChange = async (e) => {
    const value = e.target.value;
    onChange(e); 
    if (value.length >= 2) {
      const suggestions = await fetchBicSuggestions(value);
      setBicOptions(suggestions.map(bic => ({ id: bic, label: bic })));
    } else {
      setBicOptions([]);
    }
  };

  const handleSearch = async (formState) => {
    setSearchStatus(status.ONGOING);
    const query = getQueryParams(formState);
    await dispatch(searchOrganisation(query));
    setSearchStatus(status.DONE);
  };

  const addFavoriteOrganisation = async (favoriteOrgIds) => {
    setAddingFavoriteOrg(true);
    await dispatch(addFavoriteOrg(favoriteOrgIds));
    dispatch(
      setMsgInfo({
        success: true,
        msg: [`${tr('Favourite Organisation added successfully')}`],
      }),
    );
    dispatch(getFavoriteOrg());
    setAddingFavoriteOrg(false);
  };

  const selectOrg = (orgId) => {
    if (selectedOrgIds.includes(orgId)) {
      setSelectedOrgIds([...selectedOrgIds.filter((id) => id !== orgId)]);
      return;
    }
    setSelectedOrgIds([...selectedOrgIds, orgId]);
  };

  const onSubmit = () => {
    if (selectedOrgIds.length > 0) {
      addFavoriteOrganisation(selectedOrgIds);
      setSelectedOrgIds([]);
      navigate(`${baseSearchPath}`);
      window.scrollTo(0, 0);
    } else {
      const { org_country, ...data } = formData;

      handleSearch(data);
    }
  };


  const ActionButtons = ({ selectedOrgIds, isLoading }) => (
    <div className="d-flex justify-content-between">
      {search.length === 0 && searchStatus === status.DONE ? (
        <div className="d-flex" style={{ width: "100%" }}>
                <StyledButton
                  name="Search"
                  style={{ marginLeft: 'auto' }}
                   onClick={() => {
                      const { org_country, ...data } = formData;
                      handleSearch(data);
                      // window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                 
                />
                <div style={{ flex: 1 }} />
                <StyledButton 
                  style={{ marginRight: "2rem" }}
                  name="Propose creation" 
                  onClick={createOrgForm}
                />

        </div>
      ) : (
        !detailMode && (
          <StyledButton
            // style={{ marginTop: "2rem" }}
            name={`${selectedOrgIds.length > 0 ? 'Add Favorite' : 'Search'}`}
            type="submit"
            isLoading={isLoading}
          />
        )
      )}
    </div>
  );


  const registerInstituteOptions = React.useMemo(() => {
    // uniquelize the organisations
    const map = new Map();
    register_institute_options.forEach((props) => {
      const { name, org_country } = props;
      if (org_country === formData?.org_country) {
        map.set(name, props);
      }
    });
    return Array.from(map.values());
  }, [register_institute_options, formData?.org_country]);


const schoolTypeOptions = React.useMemo(() => {
  if (!school_options || school_options.length === 0) return [];
  // Extract unique school_type_detail by id
  const seen = new Set();
  return school_options
    .filter(school => school.school_type_detail && school.school_type_detail.id)
    .filter(school => {
      if (seen.has(school.school_type_detail.id)) return false;
      seen.add(school.school_type_detail.id);
      return true;
    })
    .map(school => ({
      id: school.school_type_detail.id,
      long_description_eng: school.school_type_detail.long_description_eng || '',
      long_description_deu: school.school_type_detail.long_description_deu || '',
    }));
}, [school_options]);

  React.useEffect(() => {
    if (id) {
      try {
        setFormLoading(true);
        axios
          .get(`${API}/favorite-org/${id}/`)
          .then((response) => {
            const { data } = response;
            const { organisation_data } = data;
            setFormData(organisation_data);
            // Fetch state options based on the country
            if (organisation_data.org_country) {
              getStateByCountryId(organisation_data.org_country);
            }
            // Fetch city options based on the state
            if (organisation_data.state) {
              getCityByStateId(organisation_data.state);
            }
          })
          .catch((e) => {
            setFormData({});
          })
          .finally(() => {
            setFormLoading(false);
          });
      } catch (e) {
        setFormData({});
        setFormLoading(false);
      }
    } else {
      setFormData({});
      setFormLoading(false);
    }
  }, [id]);


  const moveToOrgFunction = () => {
    const pathArray = basePath.split('/');
    pathArray.pop();
    const pathway = `${pathArray.join('/')}/org_func_assign?q=${formData?.id}`;
    return pathway;
  };

  const toggleEntryField = () => {
    setShowEntryForm(!showEntryForm);
  };

  const onEntryChange = (e) => {
    const { value, name } = e.target;
    setEntryValues({ ...entryValues, [name]: value });
  };

  const validateAndSubmitEntry = async (entryType) => {
    if (!(entryValues.name && entryValues.language)) {
      Swal.fire(
        tr('Warning!'),
        tr(`Please ensure all required fields are populated.`),
        'warning',
      );
      return;
    }
    try {
      setEntrySubmitting(true);
      const payload = {
        name: entryValues.name,
        language: entryValues.language,
        entry_type: entryType,
      };
      const response = await axios.post(`${API}/propose/entry/`, payload);
      Swal.fire(
        tr('Success!'),
        tr(`${RESPONSE_STRINGS?.ENTRY_RESPONSE_SUCCESS_MESSAGE}`),
        'success',
      );
      setEntryValues({});
      setEntrySubmitting(false);
      setShowEntryForm(false);
      return;
    } catch (error) {
      const err = getResError(error);
      dispatch(setMsgInfo({ success: false, msg: err }));
      setEntrySubmitting(false);
      setShowEntryForm(false);
    } finally {
      setEntrySubmitting(false);
      setShowEntryForm(false);
    }
  };


  return (
    
    <Wrapper className="form-wrapper">
      <div className="flex">
            <StyledButtonLink
              to={baseSearchPath}
              className="option-btn"
              icon="angle double left"
              name="All Entries"
            />
            <StyledButtonLink
              to={moveToOrgFunction()}
              className="option-btn"
              icon="angle double right"
              name={tr('Go to Org Function')}
            />
          </div>
          <div className="row">
            <div
              className="col-7 ml-10 mt-30 container"
              style={{
                position: 'relative',
                background: 'var(--admincat-color-grey-1)',
              }}
            >
          <br/>

          <OrgForm
            onSubmit={onSubmit}
            formData={formData}
            isLoading={formSubmitting}
            loadingData={formLoading}
            validationSchema={searchOrgFormSchema}
            toggleForm={() => {
              setSelectedOrgIds([]);
              resetForm();
            }}
            renderActionButtons={() =>
              renderActionButtons
                ? renderActionButtons({ selectedOrgIds, addingFavoriteOrg })
                : ActionButtons({
                    selectedOrgIds,
                    isLoading: searchStatus === status.ONGOING || addingFavoriteOrg,
                  })
            }
            title="Organisation"
          >
            {
                formData?.organisation_logo && (
                    <>
                     <ImageWrapper>
                      <Image src={formData?.organisation_logo} />
                    </ImageWrapper>
                    </>
                )
            }
            <StyledInput
              onChange={onChange}
              name="name"
              id="name"
              placeholder="Organisation"
              label="Organisation"
              value={formData?.name || ''}
              showRequiredAsterisk
            />
            <StyledSelect
              optionDefaultValue="Choose Country"
              label="Country"
              value={formData?.org_country || ''}
              name="org_country"
              id="country"
              localeOptions={countryLocaleOptions}
              onChange={(e) => {
                onChange(e);
                getStateByCountryId(e.target.value);
              }}
              deselectable
            />
            <StyledSelect
              isLoading={fetchingState}
              value={formData?.state || ''}
              name="state"
              label="State"
              placeholder="State"
              optionDefaultValue="State"
              localeOptions={stateLocaleOptions}
              onChange={(e) => {
                onChange(e);
                getCityByStateId(e.target.value);
              }}
              disabled={!formData?.org_country}
            />
            <StyledSelect
              isLoading={fetchingCity}
              onChange={onChange}
              optionDefaultValue="Choose City"
              label="City Location"
              value={formData?.city_location || ''}
              name="city_location"
              id="city_location"
              localeOptions={cityLocaleOptions}
              disabled={!formData?.state}
              deselectable
            />
            <StyledNewEntriesSelect
              value={formData?.legal_form || ''}
              name="legal_form"
              label={tr('Legal Form')}
              optionDefaultValue="Choose Legal Form"
              localeOptions={legalFormLocaleOptions}
              onChange={(e) => {
                if (e.target.value === 'proposeValue') {
                  toggleEntryField();
                  return;
                } else {
                  onChange(e);
                }
              }}
              entryDefaultLanguage={entryValues.language}
              entryValue={entryValues.name}
              entryLanguageValue={entryValues.language}
              entryValueName={tr('Legal form')}
              entryType={tr('Legal form')}
              entryLanguageName={'language'}
              onEntryChange={onEntryChange}
              onCreateNewEntry={(e) => {
                e.preventDefault();
                validateAndSubmitEntry('legal_form');
              }}
              add={showEntryForm}
              toggleEntryField={toggleEntryField}
              options={activeLanguageOptions}
              isLoading={entrySubmitting}
              closeEntryForm={() => {
                toggleEntryField();
              }}
              deselectable
            />


            <StyledSelect
              onChange={onChange}
              value={formData?.register_institute || ''}
              name="register_institute"
              label="Registration Institution"
              optionDefaultValue="Registration Institution"
              options={registerInstituteOptions}
              disabled={!formData?.org_country}
              deselectable
            />
            <StyledInput
              onChange={onChange}
              value={formData?.register_no || ''}
              name="register_no"
              label="Official Register No"
              placeholder="Official Register No"
            />

            {
                !routePathname.includes('search') && (
            <>
              <StyledInputDatePicker
                label="Liquidation"
                value={formData.liquidation}
                name="liquidation"
                onChange={onChange}
              />
              <StyledInputDatePicker
                label="Foundation"
                value={formData.foundation}
                name="foundation"
                onChange={onChange}
              />
            </>
          )
        }
            <Switchery
              name="public"
              value={formData?.public || ''}
              onChange={onChange}
              label="Public"
              showRightLabel={false}
            />
            <StyledSelect
              onChange={onChange}
              value={formData?.org_motivation || ''}
              name="org_motivation"
              label="Org Motivation"
              optionDefaultValue="Choose Org Motivation"
              localeOptions={orgMotivationLocaleOptions}
              deselectable
            />
            <Switchery
              name="legal_personality"
              value={formData?.legal_personality || ''}
              onChange={onChange}
              label="Legal Personality"
              showRightLabel={false}
            />
            <StyledSelect
              onChange={onChange}
              value={formData?.parent_org || ''}
              name="parent_org"
              label="Parent Organisation"
              optionDefaultValue="Choose Parent Organisation"
              options={orgOptions}
              deselectable
              sort={false}
            />
            <Switchery
              name="non_profit"
              value={formData?.non_profit || ''}
              onChange={onChange}
              label="Non Profit"
              showRightLabel={false}
            />
            <StyledSelect
              optionDefaultValue="Choose Industry"
              label="Industry"
              name="industry"
              id="industry"
              value={formData?.industry || ''}
              options={industryTypeOptions}
              onChange={onChange}
              deselectable
              sort={false}
            />

            <StyledSelect
              name="organisation_function"
              label="Organisation Function"
              id="organisation_function"
              value={formData?.organisation_function || ''}
              onChange={onChange}
              optionDefaultValue={tr('Choose org. function')}
              localeOptions={orgFunctionLocaleOptions}
              deselectable
            />

            {formData.organisation_function === 'school' && (
              <StyledSelect
                optionDefaultValue="Choose School Type"
                label="School Type"
                value={formData.school_type || ''}
                name="school_type"
                onChange={onChange}
                localeOptions={schoolTypeOptions}
                deselectable
              />
            )}

          {formData.organisation_function &&
            formData.organisation_function !== 'school' &&
            formData.organisation_function !== 'bank' && (
              <StyledInput
                label={getOrgFunctionLabel(formData.organisation_function)}
                name={formData.organisation_function}
                value={formData[formData.organisation_function] || ''}
                onChange={onChange}
                placeholder={`Enter ${getOrgFunctionLabel(formData.organisation_function)}`}
              />
          )}

          {formData.organisation_function === 'bank' && (
            <StyledAutoCompleteInput
              label="BIC"
              name="BIC"
              value={formData.BIC || ''}
              onChange={handleBicInputChange}
              options={bicOptions}
              placeholder="Enter BIC"
            />
          )}

            {
                !routePathname.includes('search') && (
              <>
                <StyledInput
                onChange={onChange}
                value={formData.no_of_employees}
                name="no_of_employees"
                label="No. of Employees"
                placeholder="No. of Employees"
                fieldType="number"
              />
              <StyledInput
                onChange={onChange}
                value={formData.revenue_approx}
                name="revenue_approx"
                label="Revenue approx."
                placeholder="Revenue approx."
                fieldType="number"
              />
              <StyledSelect
                onChange={onChange}
                value={formData.revenue_currency}
                name="revenue_currency"
                label="Revenue Currency"
                optionDefaultValue="Choose Revenue Currency"
                localeOptions={currencyLocaleOptions}
                deselectable
              />
              <StyledInput
                onChange={onChange}
                value={formData.website}
                name="website"
                label="Website"
                placeholder="Website"
              />
          </>
          )
        }

            <hr style={{ background: 'var(--admincat-color-grey-2)', marginRight: "2rem" }}/>


            {search.length > 0 && searchStatus === status.DONE && (
              <SearchOrgResult
                search={search}
                trObj={trObj}
                tr={tr}
                selectOrg={selectOrg}
                checkboxType={checkboxType}
              />
            )}

          {search.length === 0 && searchStatus === status.DONE && (
            <div
              className="mb-4"
              style={{
                color: 'var(--admincat-color-grey-3)',
                fontSize: '1.1rem',
                minHeight: 80,
                width: '100%',
                // marginTop: '3rem'
              }}
            >
              <div className="d-flex flex-column align-items-center justify-content-center">
                <strong>{tr('No organisation found.')}</strong>
                <span>
                  {tr('You can update your data and search again, or propose a new organisation.')}
                </span>
              </div>
            </div>
          )}

          </OrgForm>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
  withCountry,
  withState,
  withCity,
  withLegalForm,
  withIndustryType,
  withOrganisation,
  withOrganisationMotivation,
  withPublicRespArea,
  withOrganisationFunction,
  withOrgFunctionAssignment,
  withCurrency,
  withTranslation,
  withCustomRouter,
  withUserPreferences,
  withLanguage,
  withMsgInfo,
)(SearchOrgForm);
